/**
 * Lead-action contracts for the case detail panel — the two writes a CC
 * operator makes against a Lead record (vs. against a Placement):
 *
 *   - **reject** — `Lead.Status = "Rejected"`, the lead-level terminal stamp.
 *     Distinct from a per-placement decline. Used when LF decides the lead is
 *     dead on arrival or shouldn't be routed further (bad data, duplicate,
 *     client withdrew, no firm should ever try). Carries a cross-table
 *     cascade: every non-terminal placement on the lead is auto-declined
 *     (so n8n workflows stop chasing) BEFORE the lead status flips.
 *   - **editClient** — patch any subset of `LEAD_EDITABLE` (13 fields). The
 *     allowlist is the operator-mutable shape of a Lead; everything else
 *     (parser-set identifiers, lifecycle status, system fields) is off limits.
 *
 * These gates live in shared so the route enforces them server-side against
 * the live lead AND the panel uses the same source of truth to decide which
 * buttons to show. Mirrors the placement-side `placement-action.ts` pattern.
 *
 * `TERMINAL_LEAD_STATUSES` and `LEAD_EDITABLE` mirror the portal's worker.js
 * constants (lines 126 and 3783); kept identical because they are the
 * operating contract — diverging would mean a lead the portal considers dead
 * could be re-rejected from CC, or a field the portal considers immutable
 * could be patched from CC. See ADR-0064.
 *
 * **Cascade-side inheritance** (slice 5c documented behaviour, not a separate
 * ADR): the reject cascade reuses `PlacementWriter.decline` for each
 * non-terminal placement, so ADR-0064's Decision-Date null-out applies
 * automatically. That makes CC's reject strand-free even when cascading
 * through an Accepted placement — the same latent bug the direct-decline
 * fix corrected. The portal's cascade calls airtablePatch directly and
 * omits Decision Date, so its cascade carries the same stranding. CC's
 * composition correction is the same correction by a different path; the
 * 5c PR description references ADR-0064 rather than minting a new one.
 */

import { z } from "zod";

/**
 * Lead Status values from which `reject` is invalid — the lead is already
 * terminal. Mirrors portal worker.js:126. Note "Case Closed" is the funnel-
 * terminal status (set when every placement has been closed-out), not a
 * placement decision; it shows up here because the portal projects the
 * lead-level state forward to flag the lead as dead.
 *
 * `Placed` is terminal-for-rejection even though follow-up work continues
 * (Settled book / Closed) — once a firm has placed the matter you don't
 * "reject the lead"; you'd close or drop instead.
 */
export const TERMINAL_LEAD_STATUSES = [
  "Rejected",
  "Placed",
  "Case Closed",
  "Dropped",
  // #1503: a Referral kept as a Duplicate of an earlier one (its own category, apart from Rejected).
  "Duplicate",
] as const;

/** True when a lead at `status` may be rejected. The route additionally
 *  enforces a non-empty reason; that's a body concern, not a state concern. */
export function canReject(status: string | null): boolean {
  return (
    status !== null &&
    !(TERMINAL_LEAD_STATUSES as readonly string[]).includes(status)
  );
}

/** The pinned prefix the reject cascade writes into each auto-declined
 *  placement's Decline Reason. Canonical home is the contract's predicate
 *  layer (engineering/contract/predicates.ts, beside the isFirmDecision
 *  filter that EXCLUDES declines carrying it — a drifted copy would
 *  silently let admin cascades count as firm decisions). Re-exported here
 *  so every existing CC site keeps reading one constant. */
export { ADMIN_CASCADE_DECLINE_PREFIX } from "../../../../contract/predicates";

/** Body for POST /api/leads/:id/reject. The reason is required and bounded
 *  identically to placement decline (max 5000 chars); it propagates into the
 *  cascade's Decline Reason as `ADMIN_CASCADE_DECLINE_PREFIX + reason`. */
export const RejectLeadRequestSchema = z.object({
  reason: z.string().min(1).max(5000),
});
export type RejectLeadRequest = z.infer<typeof RejectLeadRequestSchema>;

/**
 * The operator-mutable shape of a Lead — the allowlist for editClient.
 * Mirrors portal `LEAD_EDITABLE`. Keys are the canonical
 * camelCase field names CC uses on the wire (the worker translates to the
 * Airtable field IDs via LEAD_FIELD_IDS); the portal uses display-name keys
 * because it patches Airtable directly. The set IS the contract — adding a
 * field here without a matching field ID + projection in the reader breaks
 * the round-trip.
 *
 * The 13 fields:
 *   - **name / phone / phone2 / email** — contact identity.
 *   - **address / residentState** — mailing + venue identity.
 *   - **caseSummary / caseType** — matter description.
 *   - **incidentDate / incidentState** — incident facts (state has the
 *     `Unmatched → Unreviewed` side effect at the route).
 *   - **intakeDocs** — the case-file attachments. Edits via the multi-
 *     attachment field (replace-the-set semantics; the panel form decides
 *     UX, the schema accepts whatever JSON Airtable accepts).
 *   - **internalNotes** — parser/operator scratch notes.
 *   - **signed** — the Transfer kill-switch. Toggleable but consequential
 *     (a flip cascades into Routing / Intro / Update workflows on the
 *     next poll). No special-case here — same allowlist member.
 */
export const LEAD_EDITABLE = [
  "name",
  "phone",
  "phone2",
  "email",
  "caseSummary",
  "caseType",
  "incidentDate",
  "incidentState",
  "residentState",
  "address",
  "intakeDocs",
  "internalNotes",
  "signed",
] as const;
export type LeadEditableKey = (typeof LEAD_EDITABLE)[number];

/**
 * Canonical option sets for the three Lead fields that are `singleSelect` in
 * Airtable (`Resident State` `fld7vTeQW6Np6rCSH`, `Incident State`
 * `flduf9qK5s7LnA6kf`, `Case Type` `fld94b4dCuNC5hOVX`). The values are the
 * exact choice names on those fields, verified live (pre-smoke pre-flight P8,
 * 2026-05-28).
 *
 * **Why these are enforced and the other text fields are not (ADR-0076).**
 * `AirtableClient.patch` sends `typecast: true` (client.ts:312) so Airtable can
 * coerce link arrays and friendly strings without the caller knowing option
 * ids. The side effect on a `singleSelect`: a value NOT already in the choice
 * set is *silently created as a new option* and the PATCH returns 200. An
 * operator who types "California" (or "ca", or a typo) into the free-text
 * edit-client form would therefore mint a junk option, the audit row would
 * log success, and the lead would carry a non-canonical value. Routing reads
 * exactly here — `eligibility.ts` matches `lead.incidentState` against firm
 * `States of Operation` and `lead.caseType` against `Case Types Handled` — so
 * a junk value makes the lead silently unroutable. The portal passes these
 * through opaquely (portal `adminEditClientInfo`) and carries the same latent corruption;
 * CC tightens (the ADR-0064 / 0070 / 0074 pattern) by validating against the
 * canonical set at the route BEFORE the value can reach Airtable.
 *
 * `US_STATE_CODES` serves both state fields — their live choice sets are the
 * same 51 USPS codes (50 states + DC), differing only in order.
 */
export const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
  "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
  "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
  "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI",
  "WY",
] as const;
export type UsStateCode = (typeof US_STATE_CODES)[number];

/** Lead `Case Type` singleSelect choices, verbatim (the LEADS field — distinct
 *  from the firm-side `Case Types Handled`, which uses its own wording). */
export const LEAD_CASE_TYPES = [
  "Personal injury",
  "Employment law",
  "Workers' comp",
  "Med mal",
  "Criminal defense",
  "Family law",
  "Lemon Law",
  "Other",
  "Nursing home abuse",
] as const;
export type LeadCaseType = (typeof LEAD_CASE_TYPES)[number];

/**
 * Body for POST /api/leads/:id/edit-client. Every field is optional; the
 * schema rejects an empty body (`.refine` at-least-one) so the route can
 * return 400 invalid_body uniformly.
 *
 * Per-field validation mirrors the portal's posture for free-text fields
 * (trimmed-but-otherwise-opaque — the operator types what they type) but
 * DIVERGES for the three `singleSelect` fields (`caseType`, `incidentState`,
 * `residentState`), which are validated against their canonical option sets —
 * see ADR-0076 and the `US_STATE_CODES` / `LEAD_CASE_TYPES` doc above for why
 * (typecast:true would otherwise mint junk options). Dates are YYYY-MM-DD;
 * intakeDocs is an attachment array (Airtable's
 * `multipleAttachments` shape — passed through, not validated structurally,
 * because the form's upload path produces canonical Airtable attachment
 * descriptors). `signed` is a boolean.
 *
 * The no-op short-circuit (every supplied value equals current) is a route
 * concern — it depends on the live lead, not the request. Mirrors slice 5b's
 * `editSettlement` pattern.
 *
 * `incidentState` change on an `Unmatched` lead also flips Status to
 * `Unreviewed` (side effect at the route — mirrors the side effect in portal `adminEditClientInfo`).
 */
export const EditClientRequestSchema = z
  .object({
    name: z.string().min(1).max(500).optional(),
    phone: z.string().max(100).optional(),
    phone2: z.string().max(100).optional(),
    // Empty string is allowed — it means "clear this field". The portal
    // doesn't validate email format on edit either (adminEditClientInfo
    // passes the field through opaquely); CC stays strict on the content
    // when present but accepts the clear, mirroring portal posture so the
    // operator can wipe a bad address without a workaround.
    email: z
      .union([z.string().email().max(500), z.literal("")])
      .optional(),
    caseSummary: z.string().max(20000).optional(),
    // singleSelect — must be a canonical Lead Case Type, or "" to clear.
    // Anything else 400s at the route (ADR-0076) before typecast:true can
    // mint a junk option.
    caseType: z.union([z.enum(LEAD_CASE_TYPES), z.literal("")]).optional(),
    // Empty string is allowed — clears the field. Same rationale as email.
    incidentDate: z
      .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal("")])
      .optional(),
    // Both are singleSelect on the same 51-code set — a valid USPS code, or
    // "" to clear (ADR-0076). incidentState additionally drives the
    // Unmatched→Unreviewed side effect at the route AND the routing match in
    // eligibility.ts, so a junk value here is the most load-bearing of the
    // three.
    incidentState: z.union([z.enum(US_STATE_CODES), z.literal("")]).optional(),
    residentState: z.union([z.enum(US_STATE_CODES), z.literal("")]).optional(),
    address: z.string().max(2000).optional(),
    // multipleAttachments shape — pass-through; the form produces canonical
    // Airtable attachment descriptors and we trust them at this boundary.
    intakeDocs: z
      .array(
        z.object({
          id: z.string().optional(),
          url: z.string().url().optional(),
          filename: z.string().optional(),
        }).passthrough(),
      )
      .optional(),
    internalNotes: z.string().max(20000).optional(),
    signed: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.phone !== undefined ||
      v.phone2 !== undefined ||
      v.email !== undefined ||
      v.caseSummary !== undefined ||
      v.caseType !== undefined ||
      v.incidentDate !== undefined ||
      v.incidentState !== undefined ||
      v.residentState !== undefined ||
      v.address !== undefined ||
      v.intakeDocs !== undefined ||
      v.internalNotes !== undefined ||
      v.signed !== undefined,
    {
      message: "Provide at least one editable field.",
    },
  );
export type EditClientRequest = z.infer<typeof EditClientRequestSchema>;
